import { kCaseLawCourtsFilePath, kLrrSourcesFilePath } from '@/constants/constants-api-paths'
import { kPaxtonAppBaseDomain } from '@/constants/constants-links'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'

// // Smallest units of court ids
// export type CourtId = {
//   [key: string]: string
// }
// export type CourtIdList = {
//   [key: string]: string[]
// }

// // Mid tier court record
// export type CourtRecord = Partial<Record<string, CourtId | CourtIdList>>

// // Types of court id arrangements under StateCourts
// export type StateCourts = CourtId | CourtIdList

// // Types of court id arrangements under FederalCourts
// export type CircuitCourts = CourtId | CourtIdList | CourtRecord

// // Response from the courts API with parent State and Federal keys
// export type CourtsResponse = {
//   State: Partial<Record<string, StateCourts>>
//   Federal: Partial<Record<string, CircuitCourts>>
// }

// Smallest units of court ids
export const CourtValueIsIdSchema = z.record(z.string())
export const CourtValueIsIdListSchema = z.record(z.array(z.string()))
export const CourtValueIsRecordSchema = z.object({})

// Response from the courts API with parent State and Federal keys
// Unions and .or() syntax are not  used because they don't work as expected.
// Smallest units of court values are used at render time to determine the type of component to render
const CourtsResponseSchema = z.object({
  State: z.record(z.any()),
  Federal: z.record(z.any()),
})
export type CourtsResponse = z.infer<typeof CourtsResponseSchema>

// LRR Response Type
export type LrrSourcesResponse = {
  [key: string]: {
    sources: LrrSourceSource[]
  }
}

export type LrrSourceSource = {
  label: string
  value: string
  path: string | undefined
  children: LrrSourcePathChild[] | undefined
  sources?: LrrSourceSource[]
}

export type LrrSourcePathChild = {
  id: string
  label: string
  path: string
  children: LrrSourcePathChild[]
}

// Define a service using a base URL and expected endpoints
export const hostedFiltersApi = createApi({
  reducerPath: 'hostedFiltersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: kPaxtonAppBaseDomain(), // Hosted on firebase hosting public files
  }),

  endpoints: (builder) => ({
    getAllCourts: builder.query<CourtsResponse, void>({
      query: () => ({
        url: kCaseLawCourtsFilePath,
        method: 'GET',
      }),
      keepUnusedDataFor: 31536000000, // 1 day - this does not need to be refetched often if the inputs do not change

      // Validate and transform the response using zod
      transformResponse: (response: unknown) => {
        // Validate and parse the response with zod, throw an error if it doesn't match the schema
        try {
          return CourtsResponseSchema.parse(response) as CourtsResponse
        } catch (error) {
          console.error('Validation error:', error)
          // Optionally, you could throw an RTK Query specific error or handle it in a way that makes sense for your app
          throw error
        }
      },

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, _) => {
        // If there's a response, get it as JSON
        const response = meta?.response
        console.log(`Fetch error - status: ${response?.status}`)

        // Report to sentry
        Sentry.captureException(new Error(`caseLawCourtsApi Fetch error - status: ${response?.status}`), {
          extra: {
            request: meta?.request,
            response: response,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
            baseQueryReturnValueStatus: baseQueryReturnValue.status,
            baseQueryReturnValueData: baseQueryReturnValue.data,
          },
        })

        return baseQueryReturnValue
      },
    }),

    getLrrSources: builder.query<LrrSourcesResponse, void>({
      query: () => ({
        url: kLrrSourcesFilePath,
        method: 'GET',
      }),
      keepUnusedDataFor: 31536000000, // 1 year - this does not need to be refetched often if the inputs do not change

      // Validate and transform the response using zod
      transformResponse: (response) => {
        try {
          return response as LrrSourcesResponse
        } catch (error) {
          console.error('Validation error:', error)
          // Optionally, you could throw an RTK Query specific error or handle it in a way that makes sense for your app
          throw error
        }
      },

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, _) => {
        // If there's a response, get it as JSON
        const response = meta?.response
        console.log(`Fetch error - status: ${response?.status}`)

        // Report to sentry
        Sentry.captureException(new Error(`caseLawCourtsApi Fetch error - status: ${response?.status}`), {
          extra: {
            request: meta?.request,
            response: response,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
            baseQueryReturnValueStatus: baseQueryReturnValue.status,
            baseQueryReturnValueData: baseQueryReturnValue.data,
          },
        })

        return baseQueryReturnValue
      },
    }),
  }),
})

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const { useGetAllCourtsQuery, useGetLrrSourcesQuery } = hostedFiltersApi
